import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * Combines multiple class names or class name objects into a single string.
 * Uses clsx for handling conditional classes and twMerge for deduplicating Tailwind classes.
 *
 * @example
 * // Basic usage with strings
 * cn("text-red-500", "bg-blue-500")
 *
 * @example
 * // Conditional classes
 * cn("text-lg", isLarge && "font-bold", { "opacity-50": isDisabled })
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a date to a locale string with fallback handling.
 *
 * @param {string | Date | null | undefined} date - The date to format
 * @param {Intl.DateTimeFormatOptions} options - Options for date formatting
 * @returns {string} The formatted date string or an empty string if the date is invalid
 */
export function formatDate(
  date: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "long",
    day: "numeric"
  }
): string {
  if (!date) return "";

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) return "";

    return dateObj.toLocaleDateString(undefined, options);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
}

/**
 * Formats a number as currency.
 *
 * @param {number | null | undefined} value - The value to format
 * @param {string} currency - The currency code
 * @returns {string} The formatted currency string or an empty string if the value is invalid
 */
export function formatCurrency(
  value: number | null | undefined,
  currency: string = "USD"
): string {
  if (value === null || value === undefined) return "";

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency
    }).format(value);
  } catch (error) {
    console.error("Error formatting currency:", error);
    return "";
  }
}
